import { Grid, Step, StepLabel, Stepper } from '@mui/material';
import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Configuration,
  CONFIGURATION_TYPE,
  getAssetsWithFilters,
  getIssueTypes,
  getProcureVendorsWithFilters,
  getProperties,
  getUsersWithFilters,
  getWorkOrderById,
  getWorkOrderPriorities,
  MIXPANEL_EVENTS,
  ProcureUser,
  updateWorkOrder as updateWorkOrderMutation,
  WORK_ORDER_CREATION_STATUS,
} from 'vendor-marketplace-shared-library';
import { useMutation, useQuery } from '@apollo/client';
import CreateWorkOrderWhat from './components/CreateWorkOrderWhat';
import CreateWorkOrderWhere from './components/CreateWorkOrderWhere';
import CreateWorkOrderWho from './components/CreateWorkOrderWho';
import CreateWorkOrderWhen from './components/CreateWorkOrderWhen';
import useAlert from '../../hooks/useAlert';
import workOrderService from '../../services/work-order.service';
import useMixpanel from '../../hooks/useMixpanel';
import { UPDATE_WORK_ORDER } from '../../error-messages';
import { getConfigurationValue } from '../../utilities/helpers';
import useConfigurations from '../../hooks/useConfigurations';

export type UpdateWorkOrderFormData = {
  propertyId: string | number;
  propertyLocation: string;
  description: string;
  title: string;
  issueTypeId: number;
  assignedUserId: string;
  procureVendorId: string;
  workOrderPriorityId: string;
  workOrderWatcherUserIds: string[];
  conciergeEnabled: boolean;
  maxBudget: number;
  dueDate: Date | null;
  workOrderAssetIds: string[];
};

export default function CreateWorkOrderFormPage() {
  const params = useParams();
  const alert = useAlert();
  const [searchParams, _] = useSearchParams();
  const { configurationsQuery } = useConfigurations();
  const { mixpanelTrack } = useMixpanel();
  const [formData, setFormData] = useState<Partial<UpdateWorkOrderFormData>>({});
  const [attachments, setAttachments] = useState([]);
  const [conciergeEnabled, setConciergeEnabled] = useState(false);
  const [maxBudget, setMaxBudget] = useState<number>(1000);
  const [workOrderWatcherIds, setWorkOrderWatcherIds] = useState<string[]>([]);
  const step = Number(searchParams.get('step'));

  const { data, loading, refetch } = useQuery(getWorkOrderById, {
    variables: {
      id: params.id,
    },
    onCompleted: data => {
      setFormData({
        propertyId: data.getWorkOrderById?.propertyId ?? '',
        propertyLocation: data.getWorkOrderById?.propertyLocation ?? '',
        description: data.getWorkOrderById?.description || '',
        title: data.getWorkOrderById?.title ?? '',
        issueTypeId: Number(data.getWorkOrderById?.issueType?.id) ?? '',
        assignedUserId: data.getWorkOrderById?.assignedUser?.id ?? '',
        procureVendorId: data.getWorkOrderById?.procureVendor?.id ?? '',
        workOrderPriorityId: data.getWorkOrderById?.workOrderPriority?.id ?? '',
        workOrderWatcherUserIds:
          data.getWorkOrderById?.workOrderWatcherUsers.map((user: ProcureUser) => user.id) ?? [],
        conciergeEnabled: data.getWorkOrderById?.conciergeEnabled ?? false,
        maxBudget: data.getWorkOrderById?.maxBudget ?? 0,
        workOrderAssetIds: data.getWorkOrderById?.assets ?? [],
      });
      setAttachments(data.getWorkOrderById?.attachments || []);
      setWorkOrderWatcherIds(
        data.getWorkOrderById?.workOrderWatcherUsers.map((user: ProcureUser) => user.id) ?? [],
      );
    },
  });

  const { data: propertiesData } = useQuery(getProperties);

  const { data: vendorData } = useQuery(getProcureVendorsWithFilters);

  const { data: workOrderPrioritiesData } = useQuery(getWorkOrderPriorities);

  const { data: issueTypeData } = useQuery(getIssueTypes);

  const { data: userData } = useQuery(getUsersWithFilters, {
    variables: { propertyIds: data?.getWorkOrderById?.propertyId },
    skip: !data?.getWorkOrderById?.propertyId,
  });

  const { data: assetData } = useQuery(getAssetsWithFilters, {
    variables: {
      propertyIds: [formData.propertyId],
      issueTypeIds: [formData.issueTypeId],
    },
  });

  const uploadAttachment = async (file: File) => {
    await workOrderService.uploadAndAddAttachmentsToWorkOrder(Number(params.id), file);
    refetch();
  };

  const onRemoveAttachment = async (attachmentId: string) => {
    await workOrderService.removeAttachmentsFromWorkOrder(Number(params.id), [
      Number(attachmentId),
    ]);
    refetch();
    mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_DELETE_ATTACHMENT_COMPLETED);
  };

  const [updateWorkOrder] = useMutation(updateWorkOrderMutation, {
    onError: error => {
      alert.setAlert({ message: error.message || UPDATE_WORK_ORDER });
    },
    onCompleted: data => {
      mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_CREATE_DETAILS_CONFIRMED);
    },
  });

  const onUpdate = async () => {
    await updateWorkOrder({
      variables: {
        id: params.id,
        input: {
          ...formData,
          maxBudget,
          conciergeEnabled,
          propertyId: Number(formData.propertyId),
          issueTypeId: Number(formData.issueTypeId) ?? '',
          assignedUserId: Number(formData.assignedUserId),
          procureVendorId: formData.procureVendorId ? Number(formData.procureVendorId) : null,
          workOrderPriorityId: Number(formData.workOrderPriorityId) ?? '',
          workOrderWatcherUserIds: formData.workOrderWatcherUserIds!.map(id => Number(id)),
          creationStatus: WORK_ORDER_CREATION_STATUS.COMPLETE,
          workOrderAssetIds:
            formData.workOrderAssetIds && formData.workOrderAssetIds.length > 0
              ? formData.workOrderAssetIds?.map(id => Number(id))
              : null,
        },
      },
    });
  };

  const userNonWarrantyWorkOrderCreationIsEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.NON_WARRANTY_WORK_ORDER_CREATION_IS_ENABLED,
    );

  const isUserNonWarrantyWorkOrderCreationIsEnabled = getConfigurationValue(
    userNonWarrantyWorkOrderCreationIsEnabledConfiguration,
  );

  const steps = [
    {
      label: 'Define task',
      show: true,
    },
    {
      label: 'Locate task',
      show: true,
    },
    {
      label: 'Assign task',
      show: true,
    },
    {
      label: 'Set due date',
      show: isUserNonWarrantyWorkOrderCreationIsEnabled,
    },
  ];

  if (loading || !data) {
    return <div>Loading...</div>;
  }

  return (
    <Grid container sx={{ padding: 2 }}>
      <Grid item xs={12}>
        <Stepper activeStep={step - 1} alternativeLabel>
          {steps.map(step => {
            if (step.show) {
              return (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              );
            }
          })}
        </Stepper>
      </Grid>
      <Grid item xs={12}>
        {step === 1 && (
          <CreateWorkOrderWhat
            workOrderId={params.id || ''}
            formData={formData}
            issueTypes={issueTypeData?.getIssueTypes ?? []}
            setFormData={setFormData}
            attachments={attachments}
            onUploadAttachment={uploadAttachment}
            onRemoveAttachment={onRemoveAttachment}
            onSubmit={onUpdate}
          />
        )}
        {step === 2 && (
          <CreateWorkOrderWhere
            workOrderId={params.id || ''}
            properties={propertiesData?.getProperties ?? []}
            assets={assetData?.getAssetsWithFilters ?? []}
            formData={formData}
            setFormData={setFormData}
            onSubmit={onUpdate}
          />
        )}
        {step === 3 && (
          <CreateWorkOrderWho
            workOrderId={params.id || ''}
            workOrderWatcherIds={workOrderWatcherIds}
            setWorkOrderWatcherIds={setWorkOrderWatcherIds}
            users={userData?.getUsersWithFilters ?? []}
            vendors={vendorData?.getProcureVendorsWithFilters ?? []}
            formData={formData}
            setFormData={setFormData}
            maxBudget={maxBudget}
            setMaxBudget={setMaxBudget}
            conciergeEnabled={conciergeEnabled}
            setConciergeEnabled={setConciergeEnabled}
            onSubmit={onUpdate}
          />
        )}
        {step === 4 && isUserNonWarrantyWorkOrderCreationIsEnabled && (
          <CreateWorkOrderWhen
            workOrderId={params.id || ''}
            priorities={workOrderPrioritiesData?.getWorkOrderPriorities ?? []}
            formData={formData}
            setFormData={setFormData}
            onSubmit={onUpdate}
          />
        )}
      </Grid>
    </Grid>
  );
}
